import { callLambda } from './lambda.js';
import { parseFigures, type AutoAmounts, type ProposedSettings, type SetupFigures } from './auto-payments.js';
import type { DeviceProof } from './wallet-key-store.js';
import type { BillingDetails } from './billing-details.js';
import { CHANGE_START_FAILED } from './auto-payment-changes.js';

// The calls behind Set up automatic payments (#25 phase 3). Each lambda checks the caller (a current signatory, holding the
// card where it names a job) and recomputes every figure; a refusal throws a LambdaError carrying the lambda's code.

// The autopay lambdas answer with their result object bare as `res` (not {code, message}); a refusal arrives as a thrown
// LambdaError, so anything that isn't an object here is a broken answer
export function answer<T>(result: { res: unknown }, fallback: string): T {
  if (!result.res || typeof result.res !== 'object') throw new Error(fallback);
  return result.res as T;
}

async function post<T>(path: string, token: string | undefined, body: unknown, fallback: string): Promise<T> {
  const result = await callLambda(`public-ledger/autopay/${path}`, 'POST', token, JSON.stringify(body));
  return answer<T>(result, fallback);
}

// Today's price per credit, the minimum purchase and the operating reserve, for the card's conversions and checks
export async function autopayFigures(token: string | undefined, companyId: string): Promise<SetupFigures> {
  const figures = parseFigures(await post<unknown>('figures', token, { companyId }, "Today's price can't be read"));
  if (!figures) throw new Error("Today's price can't be read");
  return figures;
}

// My Account: opens the company's set-up workflow, or the one already open. Its card is claimed on the card itself
export async function startAutopaySetup(
  token: string | undefined,
  companyId: string,
): Promise<{ workflowId: string | null; existing: boolean }> {
  const res = await post<{ workflowId?: unknown; existing?: unknown }>(
    'start',
    token,
    { companyId },
    'Automatic payments set-up could not be started',
  );
  return { workflowId: typeof res?.workflowId === 'string' ? res.workflowId : null, existing: res?.existing === true };
}

// A SetupIntent for an off-session card on the company's automatic payments customer
export async function autopaySetupIntent(
  token: string | undefined,
  jobId: string,
): Promise<{ clientSecret: string; setupIntentId: string }> {
  const res = await post<{ clientSecret?: unknown; setupIntentId?: unknown }>(
    'setup-intent',
    token,
    { jobId },
    'The card form could not be started',
  );
  if (typeof res?.clientSecret !== 'string' || typeof res.setupIntentId !== 'string') {
    throw new Error('The card form could not be started');
  }
  return { clientSecret: res.clientSecret, setupIntentId: res.setupIntentId };
}

// Records the card the caller saved (they become its owner), under the mandate text version the card showed
export async function confirmAutopaySetup(
  token: string | undefined,
  request: { jobId: string; setupIntentId: string; mandateTextVersion: string },
): Promise<unknown> {
  return post<unknown>('confirm-setup', token, request, 'The card could not be saved');
}

// The settings to sign, checked again by the server at today's price, with the company's billing details for the automatic
// payments Stripe customer (#41 part 5, signed with the settings); answers the recorded proposal
export async function proposeAutopaySettings(
  token: string | undefined,
  request: { jobId: string; settings: AutoAmounts; billing: BillingDetails },
): Promise<{ transactionId: string; proposed: ProposedSettings | null }> {
  const res = await post<{ transactionId?: unknown; proposed?: unknown }>(
    'propose',
    token,
    request,
    'The settings could not be proposed',
  );
  if (typeof res?.transactionId !== 'string') throw new Error('The settings could not be proposed');
  const proposed =
    typeof res.proposed === 'object' && res.proposed !== null ? (res.proposed as ProposedSettings) : null;
  return { transactionId: res.transactionId, proposed };
}

// The server found this person's signature already on the schedule (sent earlier, confirmed since): nothing to sign
export interface AlreadySignedApprove {
  alreadySigned: true;
}

export interface PreparedApproveSign {
  alreadySigned?: false;
  preparedTransactionId: string;
  // base64: signed with the person's key as they are
  bodyBytes: string;
  expiresAt: string | null;
  proposed: ProposedSettings | null;
  // The memo the server signs for (the approval's, or a card change's record); null when it sent none
  memo: string | null;
}

interface SignIds {
  transactionId: string;
  signatoryId: string;
  workflowId: string;
  jobId: string;
}

// The server-built ScheduleSign of the set-up approval, paid by nodeStream: only its body bytes come back to sign
export async function prepareApproveSignature(
  token: string | undefined,
  ids: SignIds,
): Promise<PreparedApproveSign | AlreadySignedApprove> {
  const res = await post<Record<string, unknown>>('prepare-signature', token, ids, 'The signing could not be prepared');
  if (res?.alreadySigned === true) return { alreadySigned: true };
  if (typeof res?.preparedTransactionId !== 'string' || typeof res.bodyBytes !== 'string' || !res.bodyBytes) {
    throw new Error('The signing could not be prepared');
  }
  return {
    preparedTransactionId: res.preparedTransactionId,
    bodyBytes: res.bodyBytes,
    expiresAt: typeof res.expiresAt === 'string' ? res.expiresAt : null,
    proposed: typeof res.proposed === 'object' && res.proposed !== null ? (res.proposed as ProposedSettings) : null,
    memo: typeof res.memo === 'string' ? res.memo : null,
  };
}

// Only the signature (hex) goes back, never bytes, with the signing device's proof (#24 phase 3's device gate)
export async function submitApproveSignature(
  token: string | undefined,
  request: SignIds & { preparedTransactionId: string; signature: string; device: DeviceProof },
): Promise<{ signed: boolean; fulfilled: boolean }> {
  const res = await post<{ signed?: unknown; fulfilled?: unknown }>(
    'submit-signature',
    token,
    request,
    'The signature could not be sent',
  );
  return { signed: res?.signed === true, fulfilled: res?.fulfilled === true };
}

// --- The phase 4 cards (#25): every outcome is the server's, completed by the lambda that checked the action ---

export type AutopayCardAction =
  | 'retry'
  | 'confirm'
  | 'confirmed'
  | 'close-for-month'
  // #25 phase 6: each starts a change of amounts as the card's child (the card's workflow is answered): Raise on Limit
  // reached and Raise operating target, Change on Change target
  | 'raise'
  | 'change'
  // #25 phase 6: Cancel on Raise operating target (the people waiting for the raise are told it won't be raised)
  | 'cancel';

/**
 * Why a change of amounts can't start now (#25 phase 6): a Raise the automatic payments operating target card is open.
 * `message` is the server's own line, shown as it is; with no `holderName` (unclaimed, or held by the caller) the card
 * says to raise the target there and an Open link goes to `jobId`.
 */
export interface ChangeBlocked {
  reason: string;
  jobId: string | null;
  holderName: string | null;
  message: string;
}

// The `blocked` part of a start-change or card-action answer, or null
export function changeBlocked(value: unknown): ChangeBlocked | null {
  if (typeof value !== 'object' || value === null) return null;
  const b = value as Record<string, unknown>;
  if (typeof b.message !== 'string' || !b.message.trim()) return null;
  return {
    reason: typeof b.reason === 'string' ? b.reason : '',
    jobId: typeof b.jobId === 'string' && b.jobId ? b.jobId : null,
    holderName: typeof b.holderName === 'string' && b.holderName.trim() ? b.holderName.trim() : null,
    message: b.message.trim(),
  };
}

export interface AutopayCardAnswer {
  action: string;
  // retry: whether the new charge was scheduled
  charged?: boolean;
  // confirm / confirmed: the payment intent's status, and its client secret while the bank wants a step in the browser
  status?: string;
  clientSecret?: string;
  // close-for-month: the month closed
  month?: string;
  // raise, change: the card's workflow, whose child the change is
  workflowId?: string;
  // raise, change: a change of amounts already waits for signatures (the child adopts it), and the caller's card to open
  // for it (their signing card, else the change's own card), if any
  waiting?: boolean;
  openJobId?: string;
  // raise (Limit reached), change: nothing was started and the card stays open while a Raise operating target card is
  blocked?: ChangeBlocked;
  // cancel (Raise operating target): how many waiting people were told the target won't be raised
  declined?: number;
}

// An action on a Payment failed, Limit reached, Change target or Raise operating target card; the caller must be a
// signatory holding the card. `fallback` is the error when the server answers no reason of its own
export async function autopayCardAction(
  token: string | undefined,
  jobId: string,
  action: AutopayCardAction,
  fallback = 'That could not be done',
): Promise<AutopayCardAnswer> {
  const res = await post<Record<string, unknown>>('card-action', token, { jobId, action }, fallback);
  const blocked = changeBlocked(res.blocked);
  return {
    action: typeof res.action === 'string' ? res.action : action,
    ...(typeof res.charged === 'boolean' ? { charged: res.charged } : {}),
    ...(typeof res.status === 'string' ? { status: res.status } : {}),
    ...(typeof res.clientSecret === 'string' && res.clientSecret ? { clientSecret: res.clientSecret } : {}),
    ...(typeof res.month === 'string' ? { month: res.month } : {}),
    ...(typeof res.workflowId === 'string' && res.workflowId ? { workflowId: res.workflowId } : {}),
    ...(typeof res.waiting === 'boolean' ? { waiting: res.waiting } : {}),
    ...(typeof res.openJobId === 'string' && res.openJobId ? { openJobId: res.openJobId } : {}),
    ...(blocked ? { blocked } : {}),
    ...(typeof res.declined === 'number' ? { declined: res.declined } : {}),
  };
}

// #40: asks the signatories to stop automatic payments (the Stop automatic payments card); the card is then completed
export async function proposeStop(token: string | undefined, jobId: string): Promise<{ transactionId: string }> {
  const res = await post<Record<string, unknown>>('propose-stop', token, { jobId }, 'The stop could not be proposed');
  if (typeof res.transactionId !== 'string' || !res.transactionId) throw new Error('The stop could not be proposed');
  return { transactionId: res.transactionId };
}

// Proposes the same allowance again with the live settings (the Re-sign allowance card); the card is then completed
export async function resignAllowance(
  token: string | undefined,
  jobId: string,
): Promise<{ transactionId: string; settingsVersion: number | null }> {
  const res = await post<Record<string, unknown>>('resign', token, { jobId }, 'The allowance could not be proposed');
  if (typeof res.transactionId !== 'string' || !res.transactionId)
    throw new Error('The allowance could not be proposed');
  return {
    transactionId: res.transactionId,
    settingsVersion: typeof res.settingsVersion === 'number' ? res.settingsVersion : null,
  };
}

// --- The change workflows (#25 phase 6) ---

// #40: STOP, a stop of automatic payments
export type AutopayChangeKind = 'AMOUNTS' | 'CARD' | 'STOP';

export interface StartChangeAnswer {
  workflowId: string | null;
  existing: boolean;
  // A change of that kind already waits for signatures: nothing was started, and openJobId is the card to open for it
  // (the caller's own signing card, else the change's card), or null
  waiting: boolean;
  openJobId: string | null;
  // The change's own open card, to open straight away (a new change or the open one); null when none, waiting or blocked
  jobId: string | null;
  // Nothing was started: AMOUNTS, because a Raise operating target card is open; STOP (#40), because another change
  // waits for signatures
  blocked: ChangeBlocked | null;
}

// My Account: opens a change of the company's amounts or card for the caller, or the one already open
export async function startAutopayChange(
  token: string | undefined,
  companyId: string,
  kind: AutopayChangeKind,
): Promise<StartChangeAnswer> {
  const res = await post<{
    workflowId?: unknown;
    existing?: unknown;
    waiting?: unknown;
    openJobId?: unknown;
    jobId?: unknown;
    blocked?: unknown;
  }>('start-change', token, { companyId, kind }, CHANGE_START_FAILED);
  return {
    workflowId: typeof res?.workflowId === 'string' ? res.workflowId : null,
    existing: res?.existing === true,
    waiting: res?.waiting === true,
    openJobId: typeof res?.openJobId === 'string' && res.openJobId ? res.openJobId : null,
    jobId: typeof res?.jobId === 'string' && res.jobId ? res.jobId : null,
    blocked: changeBlocked(res?.blocked),
  };
}

// A SetupIntent for the new card on the company's automatic payments customer, for the holder of a Change the automatic
// payments card job or a Payment failed job (#25 phase 6: the new card is entered on the failed payment's card itself)
export async function autopayChangeIntent(
  token: string | undefined,
  jobId: string,
): Promise<{ clientSecret: string; setupIntentId: string }> {
  const res = await post<{ clientSecret?: unknown; setupIntentId?: unknown }>(
    'change-intent',
    token,
    { jobId },
    'The card form could not be started',
  );
  if (typeof res?.clientSecret !== 'string' || typeof res.setupIntentId !== 'string') {
    throw new Error('The card form could not be started');
  }
  return { clientSecret: res.clientSecret, setupIntentId: res.setupIntentId };
}

export interface ChangedCard {
  cardBrand: string | null;
  cardLast4: string | null;
  cardExpMonth: number | null;
  cardExpYear: number | null;
}

// Holds the card the caller saved as the company's next automatic payments card and records the change (round 15: an
// account-signed public ledger record); the server then completes the job (a Change the automatic payments card job
// changed, a Payment failed job change-card), whose workflow schedules the record for the signatories to sign
export async function confirmAutopayChangeCard(
  token: string | undefined,
  request: { jobId: string; setupIntentId: string; mandateTextVersion: string },
): Promise<ChangedCard> {
  const res = await post<Record<string, unknown>>('confirm-change-card', token, request, 'The card could not be saved');
  const text = (value: unknown) => (typeof value === 'string' && value ? value : null);
  const whole = (value: unknown) => (typeof value === 'number' && Number.isSafeInteger(value) ? value : null);
  return {
    cardBrand: text(res.cardBrand),
    cardLast4: text(res.cardLast4),
    cardExpMonth: whole(res.cardExpMonth),
    cardExpYear: whole(res.cardExpYear),
  };
}

// The new amounts, checked again by the server at today's price; the server then completes the job proposed
export async function proposeAutopayChange(
  token: string | undefined,
  request: { jobId: string; settings: AutoAmounts },
): Promise<{ transactionId: string }> {
  const res = await post<{ transactionId?: unknown }>(
    'propose-change',
    token,
    request,
    'The new amounts could not be proposed',
  );
  if (typeof res?.transactionId !== 'string' || !res.transactionId) {
    throw new Error('The new amounts could not be proposed');
  }
  return { transactionId: res.transactionId };
}
